import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEOHead = ({
  title = "PricePatent - AI-Powered Patent Valuation | Instant IP Analytics",
  description = "Get instant, AI-powered patent valuations and IP analytics. Professional patent licensing insights, monetization strategies, and alternative data for IP professionals and investors.",
  keywords = "patent valuation, IP analytics, patent licensing, patent monetization, alternative data, intellectual property, patent appraisal, IP investment, patent portfolio analysis",
  ogTitle,
  ogDescription,
  ogImage = "/logo.svg",
  ogType = "website",
  twitterCard = "summary_large_image",
  canonical,
  noindex = false,
  nofollow = false,
  hreflang = [],
  structuredData = null
}) => {
  const finalTitle = title.includes('PricePatent') ? title : `${title} | PricePatent`;
  const finalOgTitle = ogTitle || finalTitle;
  const finalOgDescription = ogDescription || description;
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const finalCanonical = canonical || currentUrl;

  // Default structured data for the organization
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://pricepatent.com/#organization",
        "name": "PricePatent",
        "url": "https://pricepatent.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://pricepatent.com/logo.svg",
          "width": 400,
          "height": 100
        },
        "description": "AI-powered platform for instant patent and company valuations. Get accurate, data-driven insights in seconds.",
        "email": "pricepatent@wisdomain.com",
        "serviceArea": {
          "@type": "Place",
          "name": "Worldwide"
        },
        "areaServed": [
          {
            "@type": "Country",
            "name": "United States"
          },
          {
            "@type": "Country", 
            "name": "Europe"
          },
          {
            "@type": "Country",
            "name": "China"
          },
          {
            "@type": "Country",
            "name": "Japan"
          },
          {
            "@type": "Country",
            "name": "India"
          }
        ]
      },
      {
        "@type": "WebSite",
        "@id": "https://pricepatent.com/#website",
        "url": "https://pricepatent.com",
        "name": "PricePatent",
        "publisher": {
          "@id": "https://pricepatent.com/#organization"
        },
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://pricepatent.com/demo?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@type": "Service",
        "@id": "https://pricepatent.com/#service",
        "name": "Patent Valuation Service",
        "description": "AI-powered patent valuation and IP analytics platform providing instant, accurate valuations for patents and companies.",
        "provider": {
          "@id": "https://pricepatent.com/#organization"
        },
        "serviceType": "Patent Valuation",
        "category": "Intellectual Property Analytics",
        "audience": {
          "@type": "Audience",
          "audienceType": ["IP Professionals", "Investors", "Patent Attorneys", "Technology Companies"]
        }
      }
    ]
  };

  const finalStructuredData = structuredData || defaultStructuredData;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="PricePatent" />
      
      {/* Canonical URL */}
      {finalCanonical && <link rel="canonical" href={finalCanonical} />}
      
      {/* Robots */}
      <meta name="robots" content={`${noindex ? 'noindex' : 'index'},${nofollow ? 'nofollow' : 'follow'}`} />
      
      {/* Open Graph */}
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={finalCanonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="PricePatent" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Cards */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={finalOgTitle} />
      <meta name="twitter:description" content={finalOgDescription} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Hreflang for International SEO */}
      <link rel="alternate" hreflang="en" href="https://pricepatent.com/" />
      <link rel="alternate" hreflang="en-US" href="https://pricepatent.com/" />
      <link rel="alternate" hreflang="en-GB" href="https://pricepatent.com/" />
      <link rel="alternate" hreflang="zh-CN" href="https://pricepatent.com/" />
      <link rel="alternate" hreflang="ja" href="https://pricepatent.com/" />
      <link rel="alternate" hreflang="en-IN" href="https://pricepatent.com/" />
      <link rel="alternate" hreflang="x-default" href="https://pricepatent.com/" />
      
      {/* Additional hreflang if provided */}
      {hreflang.map((lang, index) => (
        <link key={index} rel="alternate" hreflang={lang.lang} href={lang.href} />
      ))}
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(finalStructuredData)}
      </script>
      
      {/* Additional Meta for IP/Patent Industry */}
      <meta name="application-name" content="PricePatent" />
      <meta name="theme-color" content="#2563EB" />
      <meta name="msapplication-TileColor" content="#2563EB" />
      
      {/* Language and Content */}
      <meta httpEquiv="content-language" content="en" />
      <meta name="geo.region" content="US" />
      <meta name="geo.placename" content="United States" />
    </Helmet>
  );
};

export default SEOHead; 